import { Alert } from '@apex/component-library'
import { useToast } from '@chakra-ui/react'
import axios from 'axios'
import React from 'react'
import { useMutation, useQueryClient } from 'react-query'
import {
  ScheduledTransferAchResponse,
  ScheduledTransferCashJournalResponse,
  ScheduledTransferCheckResponse,
  ScheduledTransferUpdateRequest,
  ScheduledTransferWireResponse,
  TransferDirection,
  TransferType
} from '@Types'
import { getAppConfigSetting } from '../utils'
import { SCHEDULED_TRANSFERS } from './constants'
import queryKeys from './queryKeys'

type UpdateScheduleTransfer = {
  payload: ScheduledTransferUpdateRequest
  id: string
  direction: TransferDirection
  type: TransferType
}

enum TransferTypeMap {
  ACH = 'achs',
  WIRE = 'wires',
  CASH_JOURNAL = 'journal',
  CHECK = 'check'
}

enum DirectionTypeMap {
  DEPOSIT = 'deposits',
  WITHDRAWAL = 'withdrawals'
}

type UpdateScheduledTransferResponse =
  | ScheduledTransferAchResponse
  | ScheduledTransferCheckResponse
  | ScheduledTransferCashJournalResponse
  | ScheduledTransferWireResponse

export const buildUpdateUrl = (id: string, direction: TransferDirection, type: TransferType) => {
  let updatedScheduledTransferUrl = `${getAppConfigSetting('apiUrl')}${SCHEDULED_TRANSFERS}/${TransferTypeMap[type]}`

  if ((type === 'ACH' || type === 'WIRE') && direction !== 'JOURNAL') {
    updatedScheduledTransferUrl += `/${DirectionTypeMap[direction]}/${id}/update`
  } else {
    updatedScheduledTransferUrl += `/${id}/update`
  }
  return updatedScheduledTransferUrl
}

const updatedScheduledTransfer = async ({
  payload,
  id,
  direction,
  type
}: UpdateScheduleTransfer): Promise<UpdateScheduledTransferResponse> => {
  const { data } = await axios.put(buildUpdateUrl(id, direction, type), payload, {
    withCredentials: true
  })
  return data
}

export default function useUpdateScheduledTransfer() {
  const queryClient = useQueryClient()
  const toast = useToast()

  return useMutation(updatedScheduledTransfer, {
    onMutate: () => toast.closeAll(),
    onSuccess: () => {
      queryClient.invalidateQueries(queryKeys.scheduledTransfers)
      toast({
        duration: 5000,
        position: 'top-right',
        variant: 'left-accent',
        status: 'success',
        isClosable: true,
        render: ({ onClose, id }) => (
          <Alert
            sx={{
              width: 340,
              color: 'black',
              bg: 'white',
              boxShadow: '0px 0px 5px 3px rgba(153,153,153,0.5)'
            }}
            direction='column'
            variant='left-accent'
            title='Success!'
            description='Your transfer schedule amount has been updated.'
            status='success'
            isClosable
            dismiss={onClose}
            id={id as string}
          />
        )
      })
    }
  })
}
